import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const initialCompetitors = [
  {
    id: "hubspot",
    name: "HubSpot",
    website: "hubspot.com",
    category: "direct",
    description: "Inbound marketing and sales platform for businesses",
    targetAudience: "Small to medium businesses, marketing professionals, sales teams",
    contentStrategy: "Educational content, how-to guides, industry reports, webinars",
    channels: ["LinkedIn", "Blog", "YouTube", "Email", "Webinars"],
    valueProposition: "All-in-one marketing, sales, and service platform",
    strengths: ["Strong educational content", "Comprehensive platform", "Great SEO"],
    weaknesses: ["Complex for beginners", "Expensive for small businesses"],
    lastUpdated: "2024-01-15",
  },
  {
    id: "mailchimp",
    name: "Mailchimp",
    website: "mailchimp.com",
    category: "indirect",
    description: "Email marketing and automation platform",
    targetAudience: "Small businesses, entrepreneurs, e-commerce stores",
    contentStrategy: "Simple tutorials, small business success stories, design inspiration",
    channels: ["Instagram", "Blog", "Email", "YouTube", "Pinterest"],
    valueProposition: "Easy-to-use email marketing for small businesses",
    strengths: ["User-friendly interface", "Great design", "Strong brand"],
    weaknesses: ["Limited advanced features", "Pricing increases with growth"],
    lastUpdated: "2024-01-12",
  },
  {
    id: "salesforce",
    name: "Salesforce",
    website: "salesforce.com",
    category: "aspirational",
    description: "Enterprise CRM and business automation platform",
    targetAudience: "Enterprise businesses, large sales teams, IT professionals",
    contentStrategy: "Thought leadership, industry insights, case studies, events",
    channels: ["LinkedIn", "Salesforce+", "Events", "Blog", "Podcasts"],
    valueProposition: "World's #1 CRM platform for enterprise growth",
    strengths: ["Market leader", "Comprehensive features", "Strong ecosystem"],
    weaknesses: ["Very expensive", "Complex implementation", "Steep learning curve"],
    lastUpdated: "2024-01-10",
  },
];

const channelIcons = {
  linkedin: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.761 0 5-2.239 5-5v-14c0-2.761-2.239-5-5-5zm-11 19h-3v-10h3v10zm-1.5-11.268c-.966 0-1.75-.784-1.75-1.75s.784-1.75 1.75-1.75 1.75.784 1.75 1.75-.784 1.75-1.75 1.75zm15.5 11.268h-3v-5.604c0-1.337-.025-3.063-1.868-3.063-1.868 0-2.154 1.459-2.154 2.968v5.699h-3v-10h2.881v1.367h.041c.401-.761 1.379-1.563 2.841-1.563 3.039 0 3.6 2.001 3.6 4.599v5.597z"/></svg>
  ),
  instagram: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.309.975.975 1.247 2.242 1.309 3.608.058 1.266.07 1.646.07 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.687-1.309 3.608-.975.975-2.242 1.247-3.608 1.309-1.266.058-1.646.07-4.85.07s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.309-.975-.975-1.247-2.242-1.309-3.608-.058-1.266-.07-1.646-.07-4.85s.012-3.584.07-4.85c.062-1.366.334-2.633 1.309-3.608.975-.975 2.242-1.247 3.608-1.309 1.266-.058 1.646-.07 4.85-.07zm0-2.163c-3.259 0-3.667.012-4.947.07-1.276.058-2.687.334-3.662 1.309-.975.975-1.251 2.386-1.309 3.662-.058 1.28-.07 1.688-.07 4.947s.012 3.667.07 4.947c.058 1.276.334 2.687 1.309 3.662.975.975 2.386 1.251 3.662 1.309 1.28.058 1.688.07 4.947.07s3.667-.012 4.947-.07c1.276-.058 2.687-.334 3.662-1.309.975-.975 1.251-2.386 1.309-3.662.058-1.28.07-1.688.07-4.947s-.012-3.667-.07-4.947c-.058-1.276-.334-2.687-1.309-3.662-.975-.975-2.386-1.251-3.662-1.309-1.28-.058-1.688-.07-4.947-.07zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
  ),
  facebook: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M22.675 0h-21.35c-.733 0-1.325.592-1.325 1.326v21.348c0 .733.592 1.326 1.325 1.326h11.495v-9.294h-3.128v-3.622h3.128v-2.672c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.311h3.587l-.467 3.622h-3.12v9.294h6.116c.733 0 1.325-.593 1.325-1.326v-21.349c0-.733-.592-1.326-1.325-1.326z"/></svg>
  ),
  twitter: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 4.557a9.93 9.93 0 0 1-2.828.775 4.932 4.932 0 0 0 2.165-2.724c-.951.555-2.005.959-3.127 1.184a4.916 4.916 0 0 0-8.38 4.482c-4.083-.205-7.697-2.162-10.125-5.144a4.822 4.822 0 0 0-.664 2.475c0 1.708.87 3.216 2.188 4.099a4.904 4.904 0 0 1-2.229-.616c-.054 2.281 1.581 4.415 3.949 4.89a4.936 4.936 0 0 1-2.224.084c.627 1.956 2.444 3.377 4.6 3.417a9.867 9.867 0 0 1-6.102 2.104c-.396 0-.787-.023-1.175-.069a13.945 13.945 0 0 0 7.548 2.212c9.057 0 14.009-7.513 14.009-14.009 0-.213-.005-.425-.014-.636a10.012 10.012 0 0 0 2.457-2.548z"/></svg>
  ),
  youtube: (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a2.994 2.994 0 0 0-2.112-2.112c-1.863-.502-9.386-.502-9.386-.502s-7.523 0-9.386.502a2.994 2.994 0 0 0-2.112 2.112c-.502 1.863-.502 5.754-.502 5.754s0 3.891.502 5.754a2.994 2.994 0 0 0 2.112 2.112c1.863.502 9.386.502 9.386.502s7.523 0 9.386-.502a2.994 2.994 0 0 0 2.112-2.112c.502-1.863.502-5.754.502-5.754s0-3.891-.502-5.754zm-13.498 9.568v-7.568l6.545 3.784-6.545 3.784z"/></svg>
  ),
};

const categoryColors = {
  direct: "bg-red-50 text-red-700 border-red-200",
  indirect: "bg-yellow-50 text-yellow-700 border-yellow-200",
  aspirational: "bg-blue-50 text-blue-700 border-blue-200",
};

const defaultOpportunities = [
  "Most competitors focus on LinkedIn - opportunity on Instagram and TikTok",
  "Gap in addressing work-life balance for business professionals",
  "Underutilized video content format in this space",
];
const defaultThreats = [
  "HubSpot dominates educational content space",
  "Salesforce has strong enterprise mindshare",
  "Market becoming increasingly saturated",
];

const Competitors = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [competitors, setCompetitors] = useState(initialCompetitors);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    website: "",
    category: "direct",
    description: "",
    targetAudience: "",
    contentStrategy: "",
    channels: [],
    valueProposition: "",
    strengths: [],
    weaknesses: [],
  });
  const [opportunities] = useState(defaultOpportunities);
  const [threats] = useState(defaultThreats);
  const [newChannel, setNewChannel] = useState("");
  const [newStrength, setNewStrength] = useState("");
  const [newWeakness, setNewWeakness] = useState("");

  const handleInputChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const addToList = (field, value, setValue) => {
    if (value.trim()) {
      setForm((prev) => ({ ...prev, [field]: [...prev[field], value.trim()] }));
      setValue("");
    }
  };

  const removeFromList = (field, index) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleAddCompetitor = (e) => {
    e.preventDefault();
    setCompetitors((prev) => [
      ...prev,
      {
        ...form,
        id: Date.now().toString(),
        lastUpdated: new Date().toISOString().split("T")[0],
      },
    ]);
    setForm({
      name: "",
      website: "",
      category: "direct",
      description: "",
      targetAudience: "",
      contentStrategy: "",
      channels: [],
      valueProposition: "",
      strengths: [],
      weaknesses: [],
    });
    setShowForm(false);
    setNewChannel("");
    setNewStrength("");
    setNewWeakness("");
  };

  const handleDeleteCompetitor = (id) => {
    setCompetitors((prev) => prev.filter((c) => c.id !== id));
  };

  return (
    <div className="responsive-page responsive-page--competitors min-h-screen bg-white">
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/app/audiences/${id}`)}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              Back to Audience
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Competitor Analysis</h1>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Competitor
          </button>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Direct Competitors</p>
                <p className="text-2xl font-bold text-gray-900">
                  {competitors.filter((c) => c.category === "direct").length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Indirect Competitors</p>
                <p className="text-2xl font-bold text-gray-900">
                  {competitors.filter((c) => c.category === "indirect").length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zM21 5a2 2 0 00-2-2h-4a2 2 0 00-2 2v12a4 4 0 004 4h4a2 2 0 002-2V5z" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Aspirational</p>
                <p className="text-2xl font-bold text-gray-900">
                  {competitors.filter((c) => c.category === "aspirational").length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <div>
                <p className="text-sm text-gray-600">Total Tracked</p>
                <p className="text-2xl font-bold text-gray-900">{competitors.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Inline Add Competitor Form */}
        {showForm && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Add New Competitor</h3>
              <button
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                onClick={() => setShowForm(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleAddCompetitor}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Competitor Name</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    value={form.name}
                    onChange={e => handleInputChange("name", e.target.value)}
                    placeholder="e.g., HubSpot"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Website</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    value={form.website}
                    onChange={e => handleInputChange("website", e.target.value)}
                    placeholder="e.g., hubspot.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    className="border rounded px-3 py-2 w-full"
                    value={form.category}
                    onChange={e => handleInputChange("category", e.target.value)}
                  >
                    <option value="direct">Direct Competitor</option>
                    <option value="indirect">Indirect Competitor</option>
                    <option value="aspirational">Aspirational</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Value Proposition</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    value={form.valueProposition}
                    onChange={e => handleInputChange("valueProposition", e.target.value)}
                    placeholder="Their main selling point"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    className="border rounded px-3 py-2 w-full"
                    value={form.description}
                    onChange={e => handleInputChange("description", e.target.value)}
                    rows={2}
                    placeholder="Brief description of the competitor"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Target Audience</label>
                  <textarea
                    className="border rounded px-3 py-2 w-full"
                    value={form.targetAudience}
                    onChange={e => handleInputChange("targetAudience", e.target.value)}
                    rows={2}
                    placeholder="Who they target"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Content Strategy</label>
                  <input
                    className="border rounded px-3 py-2 w-full"
                    value={form.contentStrategy}
                    onChange={e => handleInputChange("contentStrategy", e.target.value)}
                    placeholder="e.g., Educational content, webinars"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Channels</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      className="border rounded px-3 py-2 flex-1"
                      value={newChannel}
                      onChange={e => setNewChannel(e.target.value)}
                      placeholder="Add channel (e.g., LinkedIn)"
                    />
                    <button
                      type="button"
                      className="bg-gray-200 px-3 py-2 rounded"
                      onClick={() => addToList("channels", newChannel, setNewChannel)}
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {form.channels.map((ch, idx) => (
                      <span key={idx} className="bg-gray-100 px-2 py-1 rounded text-xs flex items-center gap-1">
                        {ch}
                        <button type="button" onClick={() => removeFromList("channels", idx)} className="ml-1 text-red-500">×</button>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Strengths</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      className="border rounded px-3 py-2 flex-1"
                      value={newStrength}
                      onChange={e => setNewStrength(e.target.value)}
                      placeholder="Add strength"
                    />
                    <button
                      type="button"
                      className="bg-gray-200 px-3 py-2 rounded"
                      onClick={() => addToList("strengths", newStrength, setNewStrength)}
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {form.strengths.map((s, idx) => (
                      <span key={idx} className="bg-green-100 px-2 py-1 rounded text-xs flex items-center gap-1">
                        {s}
                        <button type="button" onClick={() => removeFromList("strengths", idx)} className="ml-1 text-red-500">×</button>
                      </span>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">Weaknesses</label>
                  <div className="flex gap-2 mb-2">
                    <input
                      className="border rounded px-3 py-2 flex-1"
                      value={newWeakness}
                      onChange={e => setNewWeakness(e.target.value)}
                      placeholder="Add weakness"
                    />
                    <button
                      type="button"
                      className="bg-gray-200 px-3 py-2 rounded"
                      onClick={() => addToList("weaknesses", newWeakness, setNewWeakness)}
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {form.weaknesses.map((w, idx) => (
                      <span key={idx} className="bg-red-100 px-2 py-1 rounded text-xs flex items-center gap-1">
                        {w}
                        <button type="button" onClick={() => removeFromList("weaknesses", idx)} className="ml-1 text-red-500">×</button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  type="button"
                  className="border px-4 py-2 rounded-lg"
                  onClick={() => setShowForm(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-lg"
                >
                  Add Competitor
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Competitors List */}
        <div className="space-y-4">
          {competitors.map((competitor) => (
            <div key={competitor.id} className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                  </div>
                  <div>
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{competitor.name}</h3>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${categoryColors[competitor.category]}`}>{competitor.category}</span>
                      <a
                        href={`https://${competitor.website}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-500 hover:text-gray-700"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 13v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 3h6v6" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14L21 3" />
                        </svg>
                      </a>
                    </div>
                    <p className="text-gray-600 mb-2">{competitor.description}</p>
                    <p className="text-sm text-gray-500">Last updated: {competitor.lastUpdated}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button className="p-2 text-gray-500 hover:text-gray-700">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                  <button
                    className="p-2 text-red-500 hover:text-red-700"
                    onClick={() => handleDeleteCompetitor(competitor.id)}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Target Audience */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Target Audience
                  </h4>
                  <p className="text-sm text-gray-600">{competitor.targetAudience}</p>
                </div>

                {/* Value Proposition */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2v-8a2 2 0 012-2h2" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 3h6v6" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14L21 3" />
                    </svg>
                    Value Proposition
                  </h4>
                  <p className="text-sm text-gray-600">{competitor.valueProposition}</p>
                </div>

                {/* Content Strategy */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                    Content Strategy
                  </h4>
                  <p className="text-sm text-gray-600">{competitor.contentStrategy}</p>
                </div>

                {/* Channels */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                    </svg>
                    Channels
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {competitor.channels.map((channel, index) => (
                      <span key={index} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs border">
                        {channelIcons[channel.toLowerCase()] || channelIcons.linkedin}
                        <span>{channel}</span>
                      </span>
                    ))}
                  </div>
                </div>

                {/* Strengths */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Strengths
                  </h4>
                  <div className="space-y-1">
                    {competitor.strengths.map((strength, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                        <span className="text-sm text-gray-600">{strength}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Weaknesses */}
                <div>
                  <h4 className="font-medium text-gray-900 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Weaknesses
                  </h4>
                  <div className="space-y-1">
                    {competitor.weaknesses.map((weakness, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                        <span className="text-sm text-gray-600">{weakness}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Insights Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Key Insights & Opportunities</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-gray-900 mb-2 text-green-600">Opportunities</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                {opportunities.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2 text-red-600">Threats</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                {threats.map((item, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Competitors;
